/**
 * DQ Workbench - Trending tab.
 *
 * An early-warning radar, NOT a work queue. Two side-by-side tables - "By
 * franchise" (by_fp) and "By exception type" (by_type) - each ranked by the
 * active metric.
 *
 * CORE RULE: the headline signal is RATE OF CHANGE (delta = this window vs
 * the prior window), not absolute volume. A small FP that suddenly flares up
 * ranks just as loudly as a big one, because each table's Trend bar scales
 * its own delta against that table's max delta. The endpoint gives only
 * window-vs-prior, so the Trend bar is a single rate bar, not a sparkline.
 *
 * Controls (local state): window 24h/48h/7d and metric
 * new/blocks/notes. Rows link out to the inspector (by_fp) or the FP Grid
 * filtered by type (by_type) - navigation is via react-router Link only.
 */
import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import BaseDialog from '../../../../components/shared/dialogs/BaseDialog'

import { useDataExceptionsCtx } from '../DataExceptionsContext'
import {
  dataExceptionsApi,
  type TrendingResponse,
  type TrendingRow,
  type TrendingWindow,
  type TrendingMetric,
} from '../../../../services/admin/dataExceptionsService'
import {
  exceptionActivityApi,
  type ExceptionActivityListResponse,
} from '../../../../services/exceptionActivityApi'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import DataFetchWrapper from '../../../../components/inspector/shared/DataFetchWrapper'
import { cardChrome, SectionLabel } from '../components/chrome'
import TrendingTable from '../components/TrendingTable'
import ActivityFeed from '../components/ActivityFeed'
import AdminExceptionDrawerHost, {
  useAdminExceptionDrawer,
} from '../components/AdminExceptionDrawerHost'

const BASE = '/admin/data-exceptions'

const WINDOW_OPTIONS: { value: TrendingWindow; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '7d', label: '7d' },
]

const METRIC_OPTIONS: { value: TrendingMetric; label: string }[] = [
  { value: 'new', label: 'New exceptions' },
  { value: 'blocks', label: 'New blocks' },
  { value: 'notes', label: 'Note activity' },
]

/** Rank rows by the active metric, descending. */
const sortRows = (rows: TrendingRow[], metric: TrendingMetric): TrendingRow[] => {
  const value = (r: TrendingRow): number =>
    metric === 'blocks' ? r.new_blocks_window : metric === 'notes' ? r.notes_window : r.delta
  return [...rows].sort((a, b) => value(b) - value(a))
}

const Trending: React.FC = () => {
  const { market, refreshKey, refresh } = useDataExceptionsCtx()
  const [window, setWindow] = useState<TrendingWindow>('7d')
  const [metric, setMetric] = useState<TrendingMetric>('new')
  // Notes drill-down: the row whose note threads are being inspected, and
  // whether to filter the activity list by FP or by exception type.
  const [drill, setDrill] = useState<{ row: TrendingRow; scope: 'fp' | 'type' } | null>(null)
  const drawer = useAdminExceptionDrawer()

  const scope = useMemo(() => (market ? { market } : {}), [market])

  const { data, loading, error } = useDataFetching<TrendingResponse>({
    fetchFn: useCallback(
      () => dataExceptionsApi.getTrending({ ...scope, window, metric }),
      [scope, window, metric],
    ),
    dependencies: [market, window, metric, refreshKey],
  })

  const byFp = useMemo(() => sortRows(data?.by_fp ?? [], metric), [data, metric])
  const byType = useMemo(() => sortRows(data?.by_type ?? [], metric), [data, metric])

  // by_fp -> per-FP inspector exceptions view (row.key is the fp_code).
  const fpLinkTo = useCallback((row: TrendingRow) => `/inspect/${row.key}/exceptions`, [])
  // by_type -> FP Grid filtered by the type, preserving ?market= from context.
  const typeLinkTo = useCallback(
    (row: TrendingRow) => {
      const params = new URLSearchParams()
      params.set('type', row.key)
      if (market) params.set('market', market)
      return `${BASE}/grid?${params.toString()}`
    },
    [market],
  )

  // Note-thread drill-down: open rows with notes for the clicked FP (by_fp)
  // or exception type (by_type), busiest first. Reuses the activity endpoint;
  // idle (no request) until a Notes count is clicked.
  const drillData = useDataFetching<ExceptionActivityListResponse | null>({
    fetchFn: useCallback(() => {
      if (!drill) return Promise.resolve(null)
      const base = {
        tab: 'active' as const,
        market: market || undefined,
        sort_by: 'note_count' as const,
        sort_dir: 'desc' as const,
        page: 1,
        page_size: 50,
      }
      return exceptionActivityApi.getList(
        drill.scope === 'fp'
          ? { ...base, fp_code: drill.row.key }
          : { ...base, exception_code: drill.row.key },
      )
    }, [drill, market]),
    dependencies: [drill?.scope, drill?.row.key, market, refreshKey],
  })

  const drillItems = drillData.data?.items ?? []

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_e, v) => v && setMetric(v as TrendingMetric)}
        >
          {METRIC_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none' }}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={window}
          onChange={(_e, v) => v && setWindow(v as TrendingWindow)}
        >
          {WINDOW_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none' }}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 3 }}>
        Trend is rate of change (this window vs the one before), not total volume - a small FP flaring
        up ranks as loudly as a big one. This is an early-warning radar, not a work queue.
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionLabel>By franchise — who's flaring up</SectionLabel>
          <Card sx={cardChrome}>
            <CardContent>
              <DataFetchWrapper loading={loading} error={error} data={byFp}>
                <TrendingTable
                  rows={byFp}
                  linkTo={fpLinkTo}
                  showMarket
                  onNotesClick={(row) => setDrill({ row, scope: 'fp' })}
                />
              </DataFetchWrapper>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionLabel>By exception type — network patterns</SectionLabel>
          <Card sx={cardChrome}>
            <CardContent>
              <DataFetchWrapper loading={loading} error={error} data={byType}>
                <TrendingTable
                  rows={byType}
                  linkTo={typeLinkTo}
                  showMarket={false}
                  onNotesClick={(row) => setDrill({ row, scope: 'type' })}
                />
              </DataFetchWrapper>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <BaseDialog
        open={drill !== null}
        onClose={() => setDrill(null)}
        title={drill ? `Notes · ${drill.row.label}` : 'Notes'}
        variant="info"
        size="sm"
      >
        <DataFetchWrapper
          loading={drillData.loading}
          error={drillData.error}
          data={drillItems}
        >
          <ActivityFeed
            items={drillItems}
            onSelect={(item) => {
              setDrill(null)
              drawer.open(item)
            }}
          />
        </DataFetchWrapper>
      </BaseDialog>

      <AdminExceptionDrawerHost
        selected={drawer.selected}
        onClose={drawer.close}
        onAfterMutation={refresh}
      />
    </Box>
  )
}

export default Trending
